import React, { type PropsWithChildren, useState, useEffect } from 'react'
import { Card, Flex, Radio, Space } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { ReportStatusRow } from './ReportStatusRow'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { AssigneeIds, Team, User } from '../../../shared/domain/schedule/assignee.no-deps'
import type { Flags } from '../../../shared/cfg/flags.no-deps'
import { pick, prop, sortBy } from 'remeda'
import { InfoPopover } from '../ui/InfoPopover'

interface Props {
    statuses: ReportStatus[]
    allUsers: User[]
    allTeams: Team[]
    flow: ReportStatusFlow
    onChange: (flowChange: FlowChange) => void
    showValidationErrors: boolean
    featureFlags: Partial<Flags>
}

export interface ReportStatus {
    id?: number
    uuid?: UUID
    name: string
}

export interface FlowChange {
    statuses: ReportStatus[]
    flow: ReportStatusFlow
    remap?: {
        statusBeingDeletedIndex: number
        transferToStatusIndex?: number
    }
    isAnyAdvancedSettingsDirty: boolean
    errorCount?: number
}

export interface ReportStatusFlow {
    id: number
    defaultStatus: UUID
    finalStatuses: UUID[]
    transitions: ReportStatusFlowTransition[]
}

export interface ReportStatusFlowTransition {
    fromReportStatus: UUID
    toReportStatuses: UUID[]
    canBeChangedBy: AssigneeIds
    notifyUsersWhenStatusBecomesActive: boolean
    isEditing?: boolean
    hasErrors?: boolean
}

export function ReportStatusFlowSettings({
    allUsers,
    allTeams,
    statuses,
    flow,
    onChange,
    showValidationErrors,
    featureFlags,
}: Props) {
    const [advancedSettingOpenForStatusUUID, setAdvancedSettingOpenForStatusUUID] = useState<UUID | null>(null)
    const [isAnyAdvancedSettingsDirty, setIsAnyAdvancedSettingsDirty] = useState(false)
    const [statusRowErrors, setStatusRowErrors] = useState<Record<UUID, string[]>>({})

    const hasEmptyStatus = () => statuses.find((status) => status.name === '')
    const transitions = flow.transitions

    const onFlowChange = (change: FlowChange) => {
        const validationErrorsForExistingStatuses = pick(statusRowErrors, change.statuses.map(prop('uuid')))
        const validatedFlow = validateFlowChange(change, validationErrorsForExistingStatuses)
        onChange({
            ...validatedFlow,
        })
    }

    const setEditorDirty = (isDirty: boolean) => {
        setIsAnyAdvancedSettingsDirty(isDirty)
        onFlowChange({
            statuses,
            flow,
            isAnyAdvancedSettingsDirty: isDirty,
        })
    }

    const closeAdvancedSettings = () => {
        setEditorDirty(false)
        setAdvancedSettingOpenForStatusUUID(null)
    }

    const validateFlowChange = (change: FlowChange, errors: Record<UUID, string[]>) => {
        const newTransitions = validateReportStatusFlow(change.statuses, change.flow)
        return {
            ...change,
            statuses: change.statuses,
            flow: {
                ...change.flow,
                transitions: newTransitions.map(({ hasErrors, ...transition }) => ({
                    ...createDefaultTransition(transition.fromReportStatus),
                    ...transition,
                })),
            },
            errorCount:
                newTransitions.filter((transition) => transition.hasErrors).length +
                getStatusRowErrorCount(errors) +
                (change.isAnyAdvancedSettingsDirty ? 1 : 0),
        }
    }

    const setDefaultStatus = (newDefaultStatus: UUID) => {
        onFlowChange({
            statuses,
            flow: {
                ...flow,
                defaultStatus: newDefaultStatus,
            },
            isAnyAdvancedSettingsDirty,
        })
    }

    const setNewFlowForStatus = (
        statusId: UUID,
        canBeChangedTo: UUID[],
        canBeChangedBy: AssigneeIds,
        notifyUsersWhenStatusBecomesActive: boolean,
        { isFinalStatus = false }
    ) => {
        const cabBeChangedByAssignees = canBeChangedBy.users.concat(canBeChangedBy.teams).length > 0
        const newTransitions = updateTransition(transitions, statusId, {
            toReportStatuses: canBeChangedTo,
            canBeChangedBy,
            notifyUsersWhenStatusBecomesActive: cabBeChangedByAssignees ? notifyUsersWhenStatusBecomesActive : false,
        })

        const newFinalStatuses = isFinalStatus
            ? [...flow.finalStatuses, statusId]
            : flow.finalStatuses.filter((id) => id !== statusId)

        onFlowChange({
            statuses,
            flow: {
                ...flow,
                finalStatuses: newFinalStatuses,
                transitions: newTransitions,
            },
            isAnyAdvancedSettingsDirty: false,
        })
    }

    const setStatuses = (newStatuses: ReportStatus[]) => {
        onFlowChange({
            flow,
            statuses: newStatuses,
            isAnyAdvancedSettingsDirty,
        })
    }

    useEffect(() => {
        onFlowChange({
            flow,
            statuses,
            isAnyAdvancedSettingsDirty,
        })
    }, [statusRowErrors])

    const newStatus = () => {
        const status = { uuid: uuidv4() as UUID, name: '' }
        onFlowChange({
            flow: {
                ...flow,
                transitions: [...flow.transitions, createDefaultTransition(status.uuid)],
            },
            statuses: [...statuses, status],
            isAnyAdvancedSettingsDirty,
        })
    }

    const deleteReportStatus = (statusBeingDeletedIndex: number, transferToStatusIndex?: number) => {
        const beingDeleted = statuses.find((_, i) => i === statusBeingDeletedIndex)
        onFlowChange({
            flow: {
                ...flow,
                finalStatuses: flow.finalStatuses.filter((id) => id !== beingDeleted.uuid),
                transitions: flow.transitions.filter(({ fromReportStatus }) => fromReportStatus !== beingDeleted.uuid),
            },
            statuses: statuses.filter((_, i) => i !== statusBeingDeletedIndex),
            remap: {
                statusBeingDeletedIndex,
                transferToStatusIndex,
            },
            isAnyAdvancedSettingsDirty,
        })
    }

    const isAnyAdvancedSettingsOpen = advancedSettingOpenForStatusUUID !== null
    return (
        <Card className="report-status-container">
            <Flex>
                <Space direction="vertical" size="small" className="has-fullwidth">
                    <h3>{window.i18n('appcore:reportStatus.title')}</h3>
                    <p>{window.i18n('appcore:reportStatus.subtitle')}</p>
                    <div className="report-status-container__group">
                        <HeaderRow>
                            <span data-testid={'report-status-header-name'}>
                                {window.i18n('appcore:reportStatus.statusName')}
                            </span>
                            <span data-testid={'report-status-header-default'} style={{ flex: 1 }}>
                                <Flex gap="4px" align="center">
                                    {window.i18n('appcore:reportStatus.setDefault')}
                                    <InfoPopover title={window.i18n('appcore:reportStatus.defaultStatusInfoTooltip')} />
                                </Flex>
                            </span>
                            <span
                                style={{
                                    textTransform: 'initial',
                                    flex: featureFlags['approval-flows-multiple-finals'] ? 2 : 1,
                                }}
                            >
                                <Flex gap="4px" align="center">
                                    {window.i18n('appcore:reportStatus.canBeChangedToStatusesHeader')}
                                    <InfoPopover
                                        title={window.i18n('appcore:reportStatus.statusSelectHelperTooltip')}
                                    />
                                </Flex>
                            </span>
                            <span style={{ textTransform: 'initial', flex: 2 }}>
                                <Flex gap="4px" align="center">
                                    {window.i18n('appcore:reportStatus.canBeChangedByStatusesHeader')}
                                    <InfoPopover
                                        title={window.i18n('appcore:reportStatus.assigneeSelectHelperTooltip')}
                                    />
                                </Flex>
                            </span>
                            {featureFlags['approval-flows-notifications'] && (
                                <span
                                    style={{ textTransform: 'initial', flex: 2 }}
                                    data-testid={'report-status-header-notifications'}
                                >
                                    <Flex gap="4px" align="center">
                                        {window.i18n('appcore:reportStatus.notifications')}
                                        <InfoPopover
                                            title={window.i18n('appcore:reportStatus.notificationsHelperTooltip')}
                                        />
                                    </Flex>
                                </span>
                            )}
                            {featureFlags['approval-flows-multiple-finals'] && (
                                <span style={{ textTransform: 'initial' }}>
                                    <Flex gap="4px" align="center">
                                        {window.i18n('appcore:reportStatus.finalStatus')}
                                        <InfoPopover title={window.i18n('appcore:reportStatus.finalStatusTooltip')} />
                                    </Flex>
                                </span>
                            )}
                        </HeaderRow>
                        <Radio.Group
                            className="has-fullwidth"
                            onChange={(e) => setDefaultStatus(e.target.value)}
                            value={flow.defaultStatus}
                        >
                            <Space direction="vertical" size="small" className="has-fullwidth">
                                {sortBy(statuses, prop('id')).map((status, i) => (
                                    <ReportStatusRow
                                        key={status.uuid}
                                        isDisabled={isAnyAdvancedSettingsOpen}
                                        allUsers={allUsers}
                                        allTeams={allTeams}
                                        row={{
                                            ...status,
                                            statusIndex: i,
                                            isDefaultStatus: flow.defaultStatus === status.uuid,
                                            isFinalStatus: flow.finalStatuses.includes(status.uuid),
                                            canBeChangedToStatuses: getChangedToStatuses(status, statuses, transitions),
                                            canBeChangedByAssignees: getCanBeChangedBy(status, transitions),
                                            notifyUsersWhenStatusBecomesActive: getNotifyUsersWhenStatusBecomesActive(
                                                status,
                                                transitions
                                            ),
                                            isAdvancedSettingsOpen: advancedSettingOpenForStatusUUID === status.uuid,
                                        }}
                                        allStatuses={statuses}
                                        isAnyAdvancedSettingsDirty={isAnyAdvancedSettingsDirty}
                                        setAnyAdvancedSettingsDirty={setEditorDirty}
                                        openAdvancedSettings={() => setAdvancedSettingOpenForStatusUUID(status.uuid)}
                                        closeAdvancedSettings={closeAdvancedSettings}
                                        isAnyOtherStatusFinal={
                                            flow.finalStatuses.length && !flow.finalStatuses.includes(status.uuid)
                                        }
                                        onSave={(formData) => {
                                            setNewFlowForStatus(
                                                status.uuid,
                                                formData.canBeChangedToStatuses,
                                                formData.canBeChangedByAssignees,
                                                formData.notifyUsersWhenStatusBecomesActive,
                                                {
                                                    isFinalStatus: formData.isFinalStatus,
                                                }
                                            )
                                        }}
                                        onDeleteActionClick={(statusBeingDeletedIndex, options) => {
                                            deleteReportStatus(statusBeingDeletedIndex, options?.remapToStatusIndex)
                                        }}
                                        onNameInputChange={(newName) => {
                                            const newStatuses = [...sortBy(statuses, prop('id'))]
                                            const statusToBeChanged = newStatuses[i]
                                            statusToBeChanged.name = newName
                                            setStatuses(newStatuses)
                                        }}
                                        onNameValidationErrorChange={(errorsForChangedStatus) => {
                                            setStatusRowErrors((value) => ({
                                                ...value,
                                                [status.uuid]: errorsForChangedStatus,
                                            }))
                                        }}
                                        onCanBeChangedToChanged={(selectedStatuses: UUID[]) => {
                                            setNewFlowForStatus(
                                                status.uuid,
                                                selectedStatuses,
                                                getCanBeChangedBy(status, transitions),
                                                getNotifyUsersWhenStatusBecomesActive(status, transitions),
                                                { isFinalStatus: flow.finalStatuses.includes(status.uuid) }
                                            )
                                        }}
                                        onNotifyUsersWhenStatusBecomesActiveChanged={(
                                            notifyUsersWhenStatusBecomesActive: boolean
                                        ) => {
                                            setNewFlowForStatus(
                                                status.uuid,
                                                getChangedToStatuses(status, statuses, transitions),
                                                getCanBeChangedBy(status, transitions),
                                                notifyUsersWhenStatusBecomesActive,
                                                { isFinalStatus: flow.finalStatuses.includes(status.uuid) }
                                            )
                                        }}
                                        onCanBeChangedByChanged={(canBeChangedBy: AssigneeIds) => {
                                            setNewFlowForStatus(
                                                status.uuid,
                                                getChangedToStatuses(status, statuses, transitions),
                                                canBeChangedBy,
                                                getNotifyUsersWhenStatusBecomesActive(status, transitions),
                                                { isFinalStatus: flow.finalStatuses.includes(status.uuid) }
                                            )
                                        }}
                                        onFinalStatusChanged={() => {
                                            setNewFlowForStatus(
                                                status.uuid,
                                                getChangedToStatuses(status, statuses, transitions),
                                                getCanBeChangedBy(status, transitions),
                                                getNotifyUsersWhenStatusBecomesActive(status, transitions),
                                                { isFinalStatus: !flow.finalStatuses.includes(status.uuid) }
                                            )
                                        }}
                                        errorMessage={statusRowErrors[status.uuid]?.[0] ?? ''}
                                        autoFocusInput={!status.name}
                                        showValidationErrors={showValidationErrors}
                                        featureFlags={featureFlags}
                                    />
                                ))}

                                {!hasEmptyStatus() && <NewReportStatus onAddNewStatusClick={() => newStatus()} />}
                            </Space>
                        </Radio.Group>
                    </div>
                </Space>
            </Flex>
        </Card>
    )
}

function HeaderRow(props: PropsWithChildren<{}>) {
    return <Flex className="report-status__row report-status__row--header">{props.children}</Flex>
}

function NewReportStatus(props: { onAddNewStatusClick: () => void }) {
    return (
        <Card className="report-status__row--new" role="button" tabIndex={0} onClick={props.onAddNewStatusClick}>
            <Flex gap="middle" align="center">
                <zaptic-icon style={{ display: 'contents' }} icon-name="white-plus-sign-in-off-black-rounded-square" />

                <span>{window.i18n('appcore:reportStatus.addStatus')}</span>
            </Flex>
        </Card>
    )
}

function getCanBeChangedBy(status: ReportStatus, transitions: ReportStatusFlowTransition[]): AssigneeIds {
    return (
        transitions.find(({ fromReportStatus }) => status.uuid === fromReportStatus)?.canBeChangedBy ?? {
            teams: [],
            users: [],
        }
    )
}

function getChangedToStatuses(
    status: ReportStatus,
    allStatuses: ReportStatus[],
    transitions: ReportStatusFlowTransition[]
) {
    const changedToIds = new Set(
        transitions.find(({ fromReportStatus }) => status.uuid === fromReportStatus)?.toReportStatuses ?? []
    )
    return allStatuses.map(({ uuid }) => uuid).filter((uuid) => changedToIds.has(uuid))
}

function getNotifyUsersWhenStatusBecomesActive(status: ReportStatus, transitions: ReportStatusFlowTransition[]) {
    return transitions.find(({ fromReportStatus }) => status.uuid === fromReportStatus)
        ?.notifyUsersWhenStatusBecomesActive
}

function createDefaultTransition(fromStatusId: UUID): ReportStatusFlowTransition {
    return {
        fromReportStatus: fromStatusId,
        toReportStatuses: [],
        canBeChangedBy: { teams: [], users: [] },
        notifyUsersWhenStatusBecomesActive: false,
    }
}

function updateTransition(
    transitions: ReportStatusFlowTransition[],
    reportStatusId: UUID,
    transition: Partial<ReportStatusFlowTransition>
) {
    const originalTransition = transitions.find(({ fromReportStatus }) => fromReportStatus === reportStatusId)

    return [
        ...transitions.filter(({ fromReportStatus }) => fromReportStatus !== reportStatusId),
        {
            ...(originalTransition || createDefaultTransition(reportStatusId)),
            ...transition,
        },
    ]
}

function validateReportStatusFlow(statuses: ReportStatus[], flow: ReportStatusFlow) {
    const validatedTransitions = flow.transitions.map((transition) => ({
        ...transition,
        hasErrors: false,
    }))

    // if a report status has no transition then add a default one now
    const missingTransitions = statuses
        .filter((status) => !validatedTransitions.find(({ fromReportStatus }) => fromReportStatus === status.uuid))
        .map((status) => status.uuid)
        .map((uuid) => ({
            ...createDefaultTransition(uuid),
        }))

    return [...validatedTransitions, ...missingTransitions]
}

function getStatusRowErrorCount(statusRowErrors: Record<UUID, string[]>) {
    return Object.values(statusRowErrors).flat().length
}
